import { Briefcase, User, PlayCircle, Grid3x3, Database, Sparkles } from "lucide-react";

/**
 * Vertical IHTP Suite App Switcher.
 * Renders a fixed left-side rail with the apps (CRM, MYIHTP, Media Center, Admin DB).
 *
 * Props:
 *  - current: "crm" | "my" | "media" | "admin"
 *  - isConsultor: boolean (CRM hidden for non-consultores)
 *  - isDBAdmin: boolean (Admin DB entry hidden unless true)
 *  - theme: "light" | "dark"  (light = MYIHTP palette; dark = Media Center palette)
 */
export default function AppSwitcher({ current, isConsultor = false, isDBAdmin = false, theme = "light" }) {
  const apps = [
    { id: "crm", label: "CRM", href: "/?crm=1", icon: Briefcase, restricted: true },
    { id: "my", label: "MYIHTP", href: "/my", icon: User, restricted: false },
    { id: "media", label: "Media Center", href: "/media", icon: PlayCircle, restricted: false },
    { id: "dreams", label: "Dreams", href: "/dreams", icon: Sparkles, restricted: false },
    { id: "admin", label: "Admin DB", href: "/admin", icon: Database, adminOnly: true },
  ].filter((a) => {
    if (a.adminOnly) return isDBAdmin;
    if (a.restricted) return isConsultor;
    return true;
  });

  const isDark = theme === "dark";
  const bg = isDark ? "bg-[#0B111F]/95 border-white/5" : "bg-white border-[#E6E1D6]";
  const activeClass = isDark
    ? "bg-gradient-to-br from-[#D1CFC9] to-[#978D78] text-[#0F1829] shadow-[0_8px_24px_rgba(151,141,120,0.35)]"
    : "bg-[#0F1829] text-white shadow-[0_8px_24px_rgba(15,24,41,0.35)]";
  const idleClass = isDark
    ? "text-white/60 hover:bg-white/5 hover:text-white"
    : "text-[#978D78] hover:bg-[#F5F2EB] hover:text-[#0F1829]";

  return (
    <aside
      data-testid="app-switcher"
      className={`
        fixed z-30 ${bg} backdrop-blur-xl
        /* Desktop: left rail */
        hidden lg:flex lg:left-0 lg:top-0 lg:h-screen lg:w-[72px] lg:border-r lg:flex-col lg:items-center lg:py-5 lg:gap-4
      `}
    >
      <div className="mb-2 w-11 h-11 rounded-full bg-gradient-to-br from-[#0F1829] via-[#1a2542] to-[#978D78] grid place-items-center text-white">
        <Grid3x3 size={18} strokeWidth={1.8} />
      </div>
      <div className="w-8 h-px bg-[#E6E1D6]/50" />
      {apps.map((a) => {
        const Icon = a.icon;
        const active = a.id === current;
        return (
          <a
            key={a.id}
            href={a.href}
            data-testid={`app-switcher-${a.id}`}
            title={a.label}
            className={`group relative w-12 h-12 rounded-[20px] grid place-items-center transition-all duration-200 ${
              active ? activeClass : idleClass
            }`}
          >
            <Icon size={20} strokeWidth={1.7} />
            <span
              className={`absolute left-[calc(100%+8px)] top-1/2 -translate-y-1/2 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity ${
                isDark ? "bg-white text-[#0F1829]" : "bg-[#0F1829] text-white"
              }`}
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {a.label}
            </span>
            {active && (
              <span className="absolute -left-[3px] top-1/2 -translate-y-1/2 w-[3px] h-6 rounded-r-full bg-[#978D78]" />
            )}
          </a>
        );
      })}
    </aside>
  );
}

/**
 * Mobile bottom dock. Rendered alongside the desktop rail but only visible
 * below the `lg` breakpoint. Exported separately so layouts can adjust their
 * safe-area padding accordingly.
 */
export function AppSwitcherMobile({ current, isConsultor = false, isDBAdmin = false, theme = "light" }) {
  const apps = [
    { id: "crm", label: "CRM", href: "/?crm=1", icon: Briefcase, restricted: true },
    { id: "my", label: "MYIHTP", href: "/my", icon: User, restricted: false },
    { id: "media", label: "Media", href: "/media", icon: PlayCircle, restricted: false },
    { id: "dreams", label: "Dreams", href: "/dreams", icon: Sparkles, restricted: false },
    { id: "admin", label: "Admin", href: "/admin", icon: Database, adminOnly: true },
  ].filter((a) => {
    if (a.adminOnly) return isDBAdmin;
    if (a.restricted) return isConsultor;
    return true;
  });
  const isDark = theme === "dark";
  const bg = isDark
    ? "bg-[#0B111F]/95 border-white/5 text-white"
    : "bg-white border-[#E6E1D6] text-[#0F1829]";
  return (
    <nav
      data-testid="app-switcher-mobile"
      className={`lg:hidden fixed bottom-0 left-0 right-0 z-30 ${bg} border-t backdrop-blur-xl pb-[env(safe-area-inset-bottom)]`}
    >
      <div className={`grid ${apps.length >= 4 ? 'grid-cols-4' : 'grid-cols-3'} gap-1 px-2 py-2`}>
        {apps.map((a) => {
          const Icon = a.icon;
          const active = a.id === current;
          return (
            <a
              key={a.id}
              href={a.href}
              data-testid={`app-switcher-mobile-${a.id}`}
              className={`flex flex-col items-center gap-0.5 py-1.5 rounded-xl transition-colors ${
                active
                  ? (isDark ? "bg-white/10 text-[#D1CFC9]" : "bg-[#0F1829] text-white")
                  : (isDark ? "text-white/60" : "text-[#6B7280]")
              }`}
            >
              <Icon size={18} strokeWidth={1.8} />
              <span className="text-[10px] font-medium leading-none">{a.label}</span>
            </a>
          );
        })}
      </div>
    </nav>
  );
}
